import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { AccountingExport, AccountingExportingTypes } from '@models/matter';
import { UrlService } from '@services/url/url.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { SelectedMatter } from '../../../shared/state/matter/matter.selectors';
import { BusinessState } from '@state/business';
import { ICON } from '@core/util/icon';
import { Select, Store } from '@ngxs/store';
import { Observable, combineLatest } from 'rxjs';
import { MatterAccountingDispatch } from '@state/matter/accounting/accounting.dispatch';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { BusinessUser } from '@models/businesses';
import { getFullName } from '@core/util/string';
import { LeapExportModalComponent } from './tables/leap-export-modal/leap-export-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MatterAccountingSelectors } from '@state/matter/accounting/accounting.selectors';

@UntilDestroy()
@Component({
	selector: 'app-accounting',
	templateUrl: './accounting.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AccountingComponent implements OnInit {
	public displayedColumns: string[] = ['title', 'amount'];

	public ICON = ICON;

	public statementOfAccountUrl: string;

	public AccountingExportingTypes = AccountingExportingTypes;
	public userName: string;

	@SelectSnapshot(SelectedMatter.id) matterId: string;

	@Select(BusinessState.isSolunoEnabled) isSolunoEnabled$: Observable<boolean>;
	@Select(BusinessState.isCosmolexEnabled)
	isCosmolexEnabled$: Observable<boolean>;
	@Select(BusinessState.isLeapEnabled) isLeapEnabled$: Observable<boolean>;
	@Select(BusinessState.isGhostPracticeEnabled)
	isGhostPracticeEnabled$: Observable<boolean>;

	@Select(SelectedMatter.getAccountingExport)
	accountingExport$: Observable<AccountingExport>;
	@Select(BusinessUsersState.getActiveUsers) activeUsers$: Observable<
		BusinessUser[]
	>;

	public isSolunoEnabled: boolean;
	public isCosmolexEnabled: boolean;
	public isLeapEnabled: boolean;
	public isGhostPracticeEnabled: boolean;
	public accountingExport: AccountingExport;
	constructor(
		private matterAccountingDispatch: MatterAccountingDispatch,
		private analyticsService: AnalyticsService,
		private dialog: MatDialog,
		private store: Store,
	) {}

	ngOnInit(): void {
		this.statementOfAccountUrl = UrlService.Matter_Accounts(this.matterId);

		combineLatest([
			this.isCosmolexEnabled$,
			this.isSolunoEnabled$,
			this.isLeapEnabled$,
			this.isGhostPracticeEnabled$,
			this.accountingExport$,
			this.activeUsers$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(
				([
					isCosmolexEnabled,
					isSolunoEnabled,
					isLeapEnabled,
					isGhostPracticeEnabled,
					accountingExport,
					activeUsers,
				]) => {
					this.isCosmolexEnabled = isCosmolexEnabled;
					this.isSolunoEnabled = isSolunoEnabled;
					this.isLeapEnabled = isLeapEnabled;
					this.isGhostPracticeEnabled = isGhostPracticeEnabled;
					this.accountingExport = accountingExport;

					const user = activeUsers.find(
						(user) => user.id === accountingExport?.lastExportedBy,
					);
					if (user) {
						this.userName = user
							? getFullName([user.firstName, user.lastName])
							: '';
					}
				},
			);
	}

	export() {
		if (this.isCosmolexEnabled) {
			this.matterAccountingDispatch.ExportCosmolex();
			this.analyticsService.logEvent(AnalyticsEvent.COSMOLEX_EXPORT_CLICK);
		} else if (this.isSolunoEnabled) {
			this.matterAccountingDispatch.ExportSoluno();
			this.analyticsService.logEvent(AnalyticsEvent.SOLUNO_EXPORT_CLICK);
		} else if (this.isGhostPracticeEnabled) {
			this.matterAccountingDispatch.ExportGhostPractice();
			this.analyticsService.logEvent(
				AnalyticsEvent.GHOST_PRACTICE_EXPORT_CLICK,
			);
		} else if (this.isLeapEnabled) {
			if (this.store.selectSnapshot(MatterAccountingSelectors.getHasInvalids))
				return;

			this.dialog.open(LeapExportModalComponent);
		}
	}

	print() {
		if (this.isCosmolexEnabled) {
			this.matterAccountingDispatch.PrintCosmolex();
			this.analyticsService.logEvent(AnalyticsEvent.COSMOLEX_PRINT_CLICK);
		} else if (this.isSolunoEnabled) {
			this.matterAccountingDispatch.PrintSoluno();
			this.analyticsService.logEvent(AnalyticsEvent.SOLUNO_PRINT_CLICK);
		} else if (this.isGhostPracticeEnabled) {
			this.matterAccountingDispatch.PrintGhostPractice();
			this.analyticsService.logEvent(AnalyticsEvent.GHOST_PRACTICE_PRINT_CLICK);
		}
	}
}
